import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $transactionStore, $initialCurrenciesAvailableStore, $transactionsWithPaymentsStore } from '@lib/store';
import { useListState } from '@shared/hooks';
import { AccountUtils, cn, PaletteUtils } from '@shared/utils';
import { AccountType, DepositPaymentDirectionEnum, DepositPaymentStatusEnum, TransactionStatusEnum } from '@shared/api';
import { PaymentTable } from './paymentTable';
import { TransactionTable } from './transactionTable';
import { SearchInput, Select, Text } from '@shared/widgets';

const EXCLUDED_PAYMENT_STATUSES: DepositPaymentStatusEnum[] = [DepositPaymentStatusEnum.Failed];
const PAYMENT_STATUSES = Object.entries(DepositPaymentStatusEnum)
  .filter(([, value]) => !EXCLUDED_PAYMENT_STATUSES.includes(value))
  .map(([, value]) => ({ label: value, value }));
const TRANSACTION_STATUSES = Object.entries(TransactionStatusEnum).map(([, value]) => ({ label: value, value }));
const DIRECTION_OPTIONS = Object.entries(DepositPaymentDirectionEnum).map(([, value]) => ({ label: value, value }));

interface ListProps {
  accountType: AccountType;
  accountIds?: number[];
  search?: boolean;
  filter?: boolean;
}

export const List = (props: ListProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const paymentStore = useUnit($transactionsWithPaymentsStore.store);
  const transactionStore = useUnit($transactionStore.store);

  const [searchText, setSearchText] = useState<string>('');
  const [currency, setCurrency] = useState<string>();
  const [direction, setDirection] = useState<DepositPaymentDirectionEnum>();
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatusEnum>();
  const [paymentStatus, setPaymentStatus] = useState<DepositPaymentStatusEnum | TransactionStatusEnum>();
  const paymentsTransactionsState = useListState({ list: 'accountsPaymentsTransactions' });
  const cardsTransactionsState = useListState({ list: 'cardsTransactions' });

  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      $transactionsWithPaymentsStore.events.clear();
      $transactionStore.events.clear();
    };
  }, []);

  useEffect(() => {
    setPaymentStatus(undefined);
    setTransactionStatus(undefined);
    setDirection(undefined);
  }, [props.accountType]);

  const accountIds = useMemo(() => AccountUtils.getAccountIdsWithTransactionView(props.accountIds || []), [props.accountIds]);
  useEffect(() => {
    if (props.accountType === AccountType.Payment && !!accountIds?.length) {
      $transactionsWithPaymentsStore.events.getTransactionsWithPayments({
        AccountType: AccountType.Payment,
        AccountIds: accountIds,
        Status: paymentStatus,
        Currency: currency,
        SearchText: searchText,
        Count: paymentsTransactionsState.count,
        FromIndex: paymentsTransactionsState.fromIndex,
        Direction: direction,
        ReturnTotalCount: true,
      });
    }
  }, [props.accountType, accountIds, paymentStatus, currency, searchText, direction, paymentsTransactionsState]);

  useEffect(() => {
    if (props.accountType === AccountType.Card) {
      $transactionStore.events.getTransactions({
        AccountIds: accountIds,
        Status: transactionStatus,
        Currency: currency,
        TextSearch: searchText,
        Count: cardsTransactionsState.count,
        FromIndex: cardsTransactionsState.fromIndex,
      });
    }
  }, [props.accountType, accountIds, transactionStatus, currency, searchText, cardsTransactionsState]);

  return (
    <section
      className={cn(
        'flex w-full flex-col gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
      )}
    >
      {PaletteUtils.isInsigniaPalette && (
        <Text weight='semibold' size='2xl'>
          {t('tables.transaction.title')}
        </Text>
      )}
      {(props.search || props.filter) && (
        <div
          className={cn(
            'flex gap-12',
            'max-md:flex-col max-md:items-start max-md:gap-6',
            PaletteUtils.isVcfcPalette && 'items-center justify-between',
          )}
        >
          {PaletteUtils.isVcfcPalette && (
            <Text weight='semibold' size='lg'>
              {t('tables.transaction.title')}
            </Text>
          )}
          <div className={cn('flex gap-3', 'max-md:w-full max-md:flex-col', PaletteUtils.isInsigniaPalette && 'w-full items-center justify-between')}>
            {props.search && (
              <SearchInput
                className={cn(PaletteUtils.isInsigniaPalette && 'w-full max-w-96')}
                withDebounce
                value={searchText}
                onChange={setSearchText}
              />
            )}
            {props.filter && (
              <div className={cn('ml-auto flex gap-3', 'max-lg:ml-0 max-lg:w-full max-lg:flex-col')}>
                <Select
                  className='min-w-[160px]'
                  items={currenciesAvailableStore.data}
                  nullValue={{ isEnable: true, text: t('placeholders.allCurrencies') }}
                  value={currency}
                  onChange={setCurrency}
                />
                {props.accountType === AccountType.Card && (
                  <Select
                    className='min-w-[140px]'
                    items={TRANSACTION_STATUSES.map((option) => ({ ...option, label: t(`statuses.cardTransaction.${option.label}`) }))}
                    nullValue={{ isEnable: true, text: t('placeholders.allStatuses') }}
                    value={transactionStatus}
                    onChange={setTransactionStatus}
                  />
                )}
                {props.accountType === AccountType.Payment && (
                  <Select
                    className='min-w-[140px]'
                    items={[
                      ...PAYMENT_STATUSES.map((option) => ({ ...option, label: t(`statuses.paymentTransaction.${option.label}`) })),
                      ...TRANSACTION_STATUSES.map((option) => ({ ...option, label: t(`statuses.cardTransaction.${option.label}`) })),
                    ]}
                    nullValue={{ isEnable: true, text: t('placeholders.allStatuses') }}
                    value={paymentStatus}
                    onChange={setPaymentStatus}
                  />
                )}
                {props.accountType === AccountType.Payment && (
                  <Select
                    className='min-w-[140px]'
                    items={DIRECTION_OPTIONS.map((option) => ({ ...option, label: t(`direction.${option.label.toLowerCase()}`) }))}
                    nullValue={{ isEnable: true, text: t('placeholders.allDirections') }}
                    value={direction}
                    onChange={setDirection}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {props.accountType === AccountType.Payment && (
        <PaymentTable
          isLoading={paymentStore.loading}
          error={paymentStore.error}
          onRefresh={() => {
            if (!accountIds?.length) return;
            $transactionsWithPaymentsStore.events.getTransactionsWithPayments({
              AccountType: AccountType.Payment,
              AccountIds: accountIds,
              Status: paymentStatus,
              Currency: currency,
              SearchText: searchText,
              Count: paymentsTransactionsState.count,
              FromIndex: paymentsTransactionsState.fromIndex,
              Direction: direction,
              ReturnTotalCount: true,
            });
          }}
          data={paymentStore.data.data}
          totalItemCount={paymentStore.data.total}
          currentPage={paymentsTransactionsState.page}
          countItemPage={paymentsTransactionsState.count}
          onChangePage={paymentsTransactionsState.onChangePage}
          onChangeCountItemPage={paymentsTransactionsState.onChangeCount}
        />
      )}
      {props.accountType === AccountType.Card && (
        <TransactionTable
          isLoading={transactionStore.loading}
          error={transactionStore.error}
          onRefresh={() =>
            $transactionStore.events.getTransactions({
              AccountIds: accountIds,
              Status: transactionStatus,
              Currency: currency,
              TextSearch: searchText,
              Count: cardsTransactionsState.count,
              FromIndex: cardsTransactionsState.fromIndex,
            })
          }
          data={transactionStore.data.data}
          totalItemCount={transactionStore.data.total}
          currentPage={cardsTransactionsState.page}
          countItemPage={cardsTransactionsState.count}
          onChangePage={cardsTransactionsState.onChangePage}
          onChangeCountItemPage={cardsTransactionsState.onChangeCount}
        />
      )}
    </section>
  );
};
